import { PortableText } from "@portabletext/react";
import { graphql, Script, useStaticQuery } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { Fragment, useEffect, useState } from "react";
import NewletterModal from "../common/NewletterModal";
import AnnouncementBar from "../common/annoucementbar";
import Popup from "../common/popup";
import CookiesBanner from "./CookiesBanner";
import Footer from "./footer";
import Header from "./header";

const POPUP_SESSION_KEY = "chickenwhisperer-newsletter-popup-v1";

const Layout = ({ children }) => {
  const { sanitySiteSettings } = useStaticQuery(graphql`
    {
      sanitySiteSettings {
        cursor {
          enable
          color {
            hex
          }
        }
        announcementbar {
          enable
          bgColor {
            hex
          }
          textColor {
            hex
          }
          announcements {
            announcementText
            linkType
            link
          }
        }
        leaderboardAds {
          code
        }
        headerAdSpace {
          adName
          adImage {
            asset {
              gatsbyImageData(
                layout: FULL_WIDTH
                placeholder: BLURRED
                formats: WEBP
              )
            }
          }
          adImageMobile {
            asset {
              gatsbyImageData(
                layout: FULL_WIDTH
                placeholder: BLURRED
                formats: WEBP
              )
            }
          }
          link
        }
        header {
          logo {
            alt
            image {
              asset {
                gatsbyImageData(
                  layout: FULL_WIDTH
                  placeholder: BLURRED
                  formats: WEBP
                )
              }
            }
          }
          menuLinks {
            itemType
            category {
              name
              slug {
                current
              }
            }
            menuLink {
              current
            }
            tags {
              name
              slug {
                current
              }
              category {
                name
                slug {
                  current
                }
              }
            }
            menuTitle
            links {
              linkTitle
              linkType
              slug: linkSlug {
                current
              }
              url
              sectionId
              file {
                asset {
                  url
                }
              }
            }
          }
          ctaButtonText
          ctaButtonLink
        }
        footer {
          footerLogo {
            alt
            image {
              asset {
                gatsbyImageData(
                  layout: FULL_WIDTH
                  placeholder: BLURRED
                  formats: WEBP
                )
              }
            }
          }
          about {
            title
            description
          }
          pagesLinks {
            title
            pagesLinks {
              linkTitle
              linkType
              slug: linkSlug {
                current
              }
              url
              sectionId
              file {
                asset {
                  url
                }
              }
            }
          }
          getInTouch {
            title
            email {
              icon {
                asset {
                  gatsbyImageData(
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                    formats: WEBP
                  )
                }
              }
              text
              url
            }
            phone {
              icon {
                asset {
                  gatsbyImageData(
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                    formats: WEBP
                  )
                }
              }
              text
              url
            }
            address {
              icon {
                asset {
                  gatsbyImageData(
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                    formats: WEBP
                  )
                }
              }
              text
            }
          }
          subfooter {
            publicationLogo {
              alt
              image {
                asset {
                  gatsbyImageData(
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                    formats: WEBP
                  )
                }
              }
            }
            _rawText
            bgColor {
              hex
            }
            textColor {
              hex
            }
          }
        }
        enablePopup
        popup {
          popupDelay
          title
          description
          image {
            alt
            image {
              asset {
                gatsbyImageData(
                  layout: FULL_WIDTH
                  placeholder: BLURRED
                  formats: WEBP
                )
              }
            }
          }
          buttonText
          linkType
          slug {
            current
          }
          url
        }
        socialLinks {
          icon {
            asset {
              gatsbyImageData(
                layout: FULL_WIDTH
                placeholder: BLURRED
                formats: WEBP
              )
            }
          }
          text
          url
        }
        _rawCc
      }
    }
  `);

  const [showPopup, setShowPopup] = useState(false);
  const [subscribePopup, setSubscribePopup] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!sanitySiteSettings?.enablePopup || !sanitySiteSettings?.popup) return;
    if (sessionStorage.getItem(POPUP_SESSION_KEY)) return;

    const delay = sanitySiteSettings.popup.popupDelay ?? 3000;
    const timer = setTimeout(() => {
      if (sessionStorage.getItem(POPUP_SESSION_KEY)) return;
      sessionStorage.setItem(POPUP_SESSION_KEY, "1");
      setShowPopup(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [sanitySiteSettings?.enablePopup, sanitySiteSettings?.popup]);

  return (
    <Fragment>
      {sanitySiteSettings.announcementbar.enable && (
        <AnnouncementBar annoucementbar={sanitySiteSettings.announcementbar} />
      )}
      <Header
        headerAds={sanitySiteSettings.headerAdSpace}
        reviveAds={sanitySiteSettings.leaderboardAds}
        data={sanitySiteSettings.header}
        socialLinks={sanitySiteSettings.socialLinks}
        barEnabled={sanitySiteSettings.announcementbar.enable}
      />
      {children}
      <Footer
        footer={sanitySiteSettings.footer}
        cc={sanitySiteSettings._rawCc}
        socialLinks={sanitySiteSettings.socialLinks}
      />
      {sanitySiteSettings.footer.subfooter && (
        <div
          className="border-t w-full h-full border-t-gray-200 border-solid py-3"
          style={{
            backgroundColor: sanitySiteSettings.footer.subfooter?.bgColor?.hex,
          }}
        >
          <div className="container  text-black">
            <div className="flex w-full h-full min-h-[40px] items-center space-x-2 text-xs md:text-base">
              <div className="w-[100px] h-[40px]">
                <GatsbyImage
                  image={
                    sanitySiteSettings.footer.subfooter.publicationLogo.image
                      .asset.gatsbyImageData
                  }
                  alt={sanitySiteSettings.footer.subfooter.publicationLogo.alt}
                  width={100}
                  height={100}
                  className="object-contain"
                  loading="lazy"
                />
              </div>
              <PortableText
                value={sanitySiteSettings.footer.subfooter._rawText}
                components={{
                  block: {
                    normal: ({ children, value }) => {
                      return (
                        <p
                          style={{
                            color:
                              sanitySiteSettings.footer.subfooter?.textColor
                                ?.hex,
                          }}
                          className="mb-0 text-sm md:text-base"
                        >
                          {children}
                        </p>
                      );
                    },
                  },
                  marks: {
                    link: ({ children, value }) => {
                      const rel = !value.href.startsWith("/")
                        ? "noreferrer noopener"
                        : undefined;

                      return (
                        <a
                          className="text-inherit hover:underline underline-offset-[5px] hover:text-secondary"
                          href={value.href}
                          rel={rel}
                          target="_blank"
                        >
                          {children}
                        </a>
                      );
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}

      {sanitySiteSettings.enablePopup && sanitySiteSettings.popup && (
        <Popup
          onClose={() => setShowPopup(false)}
          value={showPopup}
          logo={sanitySiteSettings.header.logo}
          data={sanitySiteSettings.popup}
          setPopup={setSubscribePopup}
        />
      )}

      {subscribePopup && (
        <NewletterModal
          closeModal={() => {
            setSubscribePopup(false);
            setShowPopup(false);
          }}
        />
      )}
      <Script
        id="clarity-script"
        strategy="post-hydrate"
      // type="text/javascript"
      >
        {`(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "p0jwma8zbq");`}
      </Script>
      <CookiesBanner />
    </Fragment>
  );
};

export default Layout;
